/* ============================================================
   Rolex Bracelet Guide — editorial / search-first
   Vanilla JS. Boots one or more guide sections per page.
   ============================================================ */
(function () {
  'use strict';

  document.querySelectorAll('[data-rolex-guide]').forEach(boot);

  function boot(root) {
    const dataUrl = root.dataset.dataUrl;
    const sellUrl = root.dataset.sellUrl || '/pages/sell-your-watch';
    const buyUrl  = root.dataset.buyUrl  || '/collections/rolex';

    const $form   = root.querySelector('[data-rolex-search-form]');
    const $input  = root.querySelector('[data-rolex-search]');
    const $hint   = root.querySelector('[data-rolex-hint]');
    const $result = root.querySelector('[data-rolex-result]');
    const $filters = root.querySelector('[data-rolex-filters]');
    const $tbody   = root.querySelector('[data-rolex-table-body]');

    let DATA = null;
    let CURRENT_FILTER = 'All';

    // Outline line-art constants
    const BH = 100;             // bracelet width (svg units, vertical)
    const DARK = '#f4f1ea';     // primary stroke (light, on the dark WE ground)
    const GOLD = '#c7a55a';     // gold stroke for precious-metal bracelets
    const SW = 2.2;

    fetch(dataUrl, { credentials: 'omit', cache: 'force-cache' })
      .then(r => r.json())
      .then(data => {
        DATA = data;
        if ($filters && $tbody) renderTable();
        // Deep-link via hash: #ref=126610LN
        const m = (location.hash || '').match(/ref=([^&]+)/i);
        if (m && $input) {
          $input.value = decodeURIComponent(m[1]).toUpperCase();
          lookup($input.value);
        }
      })
      .catch(err => {
        console.error('[rolex-bracelet-guide] data load failed', err);
        if ($hint) $hint.textContent = 'Reference data is temporarily unavailable. Please try again shortly.';
      });

    if ($form) {
      $form.addEventListener('submit', ev => {
        ev.preventDefault();
        lookup(($input.value || '').trim());
      });
    }
    if ($input) {
      // Live search as the user types (after 2 chars)
      let t;
      $input.addEventListener('input', () => {
        clearTimeout(t);
        const q = ($input.value || '').trim();
        if (q.length < 2) { hideResult(); return; }
        t = setTimeout(() => lookup(q), 120);
      });
    }

    // -------- Lookup + result rendering --------
    function lookup(query) {
      if (!DATA || !query) { hideResult(); return; }
      const q = query.toUpperCase().replace(/\s+/g, '');
      // Match references (exact or contained), then model/collection fuzzy
      let matches = (DATA.entries || []).filter(e =>
        (e.references || []).some(r => r.toUpperCase().replace(/\s+/g, '').includes(q))
      );
      if (!matches.length) {
        matches = (DATA.entries || []).filter(e =>
          (e.model + ' ' + e.collection + ' ' + e.bracelet_type).toUpperCase().includes(q)
        );
      }
      if (!matches.length) { renderNoMatch(query); return; }
      renderResult(matches[0], matches);
    }

    function hideResult() {
      if (!$result) return;
      $result.hidden = true;
      $result.innerHTML = '';
    }

    function renderNoMatch(query) {
      $result.hidden = false;
      $result.removeAttribute('data-bracelet-tone');
      $result.innerHTML = `
        <p class="rolex-guide__result-eyebrow">No Match</p>
        <h3 class="rolex-guide__result-model">Reference not in catalogue</h3>
        <hr class="rolex-guide__result-divider">
        <p class="rolex-guide__result-body">
          We don't have <strong>${esc(query)}</strong> in our verified database yet. If it's vintage,
          rare, or a configuration we haven't catalogued, send it over and our specialists will
          verify it within one business day.
        </p>
        <div class="rolex-guide__result-ctas">
          <a class="rolex-guide__result-cta rolex-guide__result-cta--primary" href="${esc(appendQuery(sellUrl, { intent: 'verify_reference', ref: query }))}">Submit a Reference</a>
        </div>
      `;
    }

    function renderResult(entry, allMatches) {
      const isFlex = entry.bracelet_type === 'Oysterflex' || entry.center_links === 0;
      // center_links = total removable links; split across both sides of the clasp.
      const total = entry.center_links | 0;
      const left  = Math.ceil(total / 2);
      const right = Math.floor(total / 2);
      const endsPerSide = entry.end_links_per_side | 0;
      const tone = braceletTone(entry);
      const isScrew = ['Jubilee', 'President', 'Pearlmaster'].includes(entry.bracelet_type);
      const refDisplay = entry.references[0].replace(/-OYSTER$|-JUBILEE$/i, '');
      const variantHint = allMatches.length > 1
        ? `<p class="rolex-guide__search-hint" style="margin-top:8px;font-size:11px">Showing ${esc(entry.bracelet_type)} variant. Other configurations exist for this reference — see the table below.</p>`
        : '';

      const linkBars = isFlex ? renderOysterflex() : renderLinkBars(entry.bracelet_type, left, right, entry);

      const headlineValue = isFlex
        ? 'OYSTERFLEX'
        : `${total} REMOVABLE`;
      const headlineSub = isFlex
        ? 'no removable links'
        : `${left} + ${right} either side of the clasp`;
      const badge = isFlex
        ? ''
        : `<span class="rolex-guide__result-badge">${isScrew ? 'Screw links — removable' : 'Pin links — removable'}</span>`;

      const body = isFlex
        ? `The Oysterflex is a flexible titanium-nickel blade encased in elastomer. There are no removable metal links — sizing is handled by the clasp's micro-adjustment.`
        : `Each link is individually ${isScrew ? 'screw-fastened' : 'pin-fastened'}. There are <strong>${total} removable links</strong> in total — roughly ${left} on one side and ${right} on the other — plus ${endsPerSide} fixed end link${endsPerSide === 1 ? '' : 's'} per side at the lugs${entry.notes && entry.notes.trim() ? '. ' + esc(entry.notes) : '.'}`;

      $result.hidden = false;
      $result.setAttribute('data-bracelet-tone', tone);
      $result.innerHTML = `
        <p class="rolex-guide__result-eyebrow">Reference ${esc(refDisplay)}</p>
        <h3 class="rolex-guide__result-model">${esc(entry.model)}</h3>
        <hr class="rolex-guide__result-divider">

        <div class="rolex-guide__result-grid">
          <div>
            <p class="rolex-guide__cell-label">${esc(entry.bracelet_type)} Bracelet</p>
            <p class="rolex-guide__cell-value">${esc(braceletShortName(entry))}</p>
            <p class="rolex-guide__cell-sub">${esc(entry.bracelet_reference || '—')}</p>
          </div>
          <div>
            <p class="rolex-guide__cell-label">${isFlex ? 'Adjustment' : 'Removable Links'}</p>
            <p class="rolex-guide__cell-value">${esc(headlineValue)}</p>
            <p class="rolex-guide__cell-sub">${esc(headlineSub)}</p>
            ${badge}
          </div>
        </div>

        <div class="rolex-guide__bars-wrap">
          ${linkBars}
          ${isFlex ? '' : `
            <div class="rolex-guide__bars-legend">
              <span class="rolex-guide__bars-legend-left">${left} links</span>
              <span class="rolex-guide__bars-legend-mid">Clasp</span>
              <span class="rolex-guide__bars-legend-right">${right} links</span>
            </div>
          `}
        </div>

        <p class="rolex-guide__result-body">${body}</p>

        <div class="rolex-guide__result-specs">
          <div class="rolex-guide__result-spec">Case Size <strong>${esc(entry.case_size_mm)}mm</strong></div>
          <div class="rolex-guide__result-spec">Material <strong>${esc(entry.material || '—')}</strong></div>
          <div class="rolex-guide__result-spec">Years <strong>${esc(entry.years || '—')}</strong></div>
          <div class="rolex-guide__result-spec">Clasp <strong>${esc(entry.clasp || '—')}</strong></div>
        </div>

        <div class="rolex-guide__result-ctas">
          <a class="rolex-guide__result-cta rolex-guide__result-cta--primary" href="${esc(appendQuery(sellUrl, { ref: refDisplay, model: entry.model }))}">Selling this? Get an Offer</a>
          <a class="rolex-guide__result-cta" href="${esc(appendQuery(buyUrl, { ref: refDisplay }))}">See Our Rolex Inventory</a>
        </div>
        ${variantHint}
      `;
    }

    // -------- Reference table --------
    function renderTable() {
      const collections = ['All'].concat(DATA.collections || []);
      $filters.innerHTML = collections.map(c =>
        `<button type="button" class="rolex-guide__filter-btn ${c === CURRENT_FILTER ? 'rolex-guide__filter-btn--active' : ''}" data-filter="${esc(c)}">${esc(c)}</button>`
      ).join('');
      $filters.addEventListener('click', ev => {
        const btn = ev.target.closest('[data-filter]');
        if (!btn) return;
        CURRENT_FILTER = btn.dataset.filter;
        $filters.querySelectorAll('.rolex-guide__filter-btn').forEach(b => {
          b.classList.toggle('rolex-guide__filter-btn--active', b.dataset.filter === CURRENT_FILTER);
        });
        renderTableBody();
      }, { once: false });
      renderTableBody();
    }

    function renderTableBody() {
      const entries = (DATA.entries || []).filter(e =>
        CURRENT_FILTER === 'All' || e.collection === CURRENT_FILTER
      );

      // Group by (collection, model). Each group can have multiple bracelet variants.
      const groups = new Map();
      entries.forEach(e => {
        const key = `${e.collection}::${e.model}`;
        if (!groups.has(key)) groups.set(key, { model: e.model, refs: new Set(), variants: [] });
        const g = groups.get(key);
        (e.references || []).forEach(r => g.refs.add(r.replace(/-OYSTER$|-JUBILEE$/i, '')));
        g.variants.push(e);
      });

      if (!groups.size) {
        $tbody.innerHTML = `<tr><td colspan="3" class="rolex-guide__table-empty">No references catalogued for ${esc(CURRENT_FILTER)} yet.</td></tr>`;
        return;
      }

      const rows = [];
      for (const g of groups.values()) {
        const refsHtml = [...g.refs].map(r => esc(r)).join('<span class="rolex-guide__td-refs-sep">·</span>');
        const linksHtml = g.variants.map(v => formatLinksCell(v)).join('');
        rows.push(`
          <tr>
            <td class="rolex-guide__td-model">${esc(g.model)}</td>
            <td class="rolex-guide__td-refs">${refsHtml}</td>
            <td class="rolex-guide__td-links">${linksHtml}</td>
          </tr>
        `);
      }
      $tbody.innerHTML = rows.join('');
    }

    function formatLinksCell(v) {
      const isFlex = v.bracelet_type === 'Oysterflex' || v.center_links === 0;
      if (isFlex) {
        return `<span class="rolex-guide__td-links-row"><strong>Oysterflex</strong></span>`;
      }
      const big = v.center_links;
      const sm  = v.end_links_per_side * 2;
      const total = big + sm;
      const isScrew = ['Jubilee', 'President', 'Pearlmaster'].includes(v.bracelet_type);
      const variantLabel = v.bracelet_type;
      return `
        <span class="rolex-guide__td-links-row">
          ${esc(variantLabel)} —
          <span class="rolex-guide__td-links-total">${total}</span>
          <span class="rolex-guide__td-links-split">(${big} + ${sm})</span>
          ${isScrew ? '<span class="rolex-guide__td-badge">Removable</span>' : ''}
        </span>
      `;
    }

    // -------- Photo-tile bracelet compositor --------
    // Builds an SVG that stamps real bracelet component photos:
    //   end-left + N×link (center-row) + clasp + N×link (outer-row) + end-right
    // The whole strip scales to fit the card via the viewBox.
    // ---- Pure-outline technical line-art ----
    const LINKW  = { oyster: 30, jubilee: 23, president: 34, pearlmaster: 23 };
    const ENDW   = { oyster: 34, jubilee: 30, president: 34, pearlmaster: 30 };
    const CLASPW = { oyster: 150, jubilee: 132, president: 152, pearlmaster: 132 };

    function rr(x, y, w, h, rx, stroke, sw) {
      return `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${w.toFixed(1)}" height="${h.toFixed(1)}" rx="${rx.toFixed(1)}" fill="none" stroke="${stroke}" stroke-width="${sw || SW}"/>`;
    }

    function linkShape(type, x, lw, stroke) {
      if (type === 'jubilee' || type === 'pearlmaster') {
        const g = 2.5, inner = lw - g;
        return [[11,15],[28,13],[43,14],[59,13],[74,15]]
          .map(([y,h]) => rr(x+g/2, y, inner, h, 4, stroke)).join('');
      }
      if (type === 'president') {
        const g = 3, inner = lw - g;
        return rr(x+g/2,13,inner,19,7,stroke) + rr(x+g/2,35,inner,30,13,stroke) + rr(x+g/2,68,inner,19,7,stroke);
      }
      // oyster
      const g = 3, inner = lw - g;
      return rr(x+g/2,11,inner,21,6,stroke) + rr(x+g/2,36,inner,28,7,stroke) + rr(x+g/2,68,inner,21,6,stroke);
    }

    function endPiece(x, ew, stroke, flip) {
      let d, notchX;
      if (!flip) {
        const ix = x + ew, ox = x;
        d = `M${ix.toFixed(1)},11 L${(ox+8).toFixed(1)},11 Q${ox.toFixed(1)},14 ${ox.toFixed(1)},26 L${ox.toFixed(1)},74 Q${ox.toFixed(1)},86 ${(ox+8).toFixed(1)},89 L${ix.toFixed(1)},89 Z`;
        notchX = ox + 4;
      } else {
        const ix = x, ox = x + ew;
        d = `M${ix.toFixed(1)},11 L${(ox-8).toFixed(1)},11 Q${ox.toFixed(1)},14 ${ox.toFixed(1)},26 L${ox.toFixed(1)},74 Q${ox.toFixed(1)},86 ${(ox-8).toFixed(1)},89 L${ix.toFixed(1)},89 Z`;
        notchX = ox - 4;
      }
      return `<path d="${d}" fill="none" stroke="${stroke}" stroke-width="${SW}" stroke-linejoin="round"/>`
        + `<line x1="${notchX.toFixed(1)}" y1="30" x2="${notchX.toFixed(1)}" y2="70" stroke="${stroke}" stroke-width="1.4" opacity="0.6"/>`;
    }

    function claspShape(x, cw, stroke, crown) {
      const body = rr(x, 15, cw, 70, 9, stroke);
      const cx = x + cw/2, cy = 50;
      let dots = '';
      [-10,-5,0,5,10].forEach(dx => { dots += `<circle cx="${(cx+dx).toFixed(1)}" cy="${(cy-7).toFixed(1)}" r="2.4" fill="${crown}"/>`; });
      const crownG = dots
        + `<path d="M${(cx-11).toFixed(1)},${(cy-5).toFixed(1)} L${(cx-9).toFixed(1)},${(cy+6).toFixed(1)} L${(cx+9).toFixed(1)},${(cy+6).toFixed(1)} L${(cx+11).toFixed(1)},${(cy-5).toFixed(1)} Z" fill="none" stroke="${crown}" stroke-width="1.7" stroke-linejoin="round"/>`
        + `<line x1="${(cx-10).toFixed(1)}" y1="${(cy+9).toFixed(1)}" x2="${(cx+10).toFixed(1)}" y2="${(cy+9).toFixed(1)}" stroke="${crown}" stroke-width="1.7"/>`;
      const bevel = `<line x1="${(x+8).toFixed(1)}" y1="24" x2="${(x+cw-8).toFixed(1)}" y2="24" stroke="${stroke}" stroke-width="1.2" opacity="0.45"/>`
        + `<line x1="${(x+8).toFixed(1)}" y1="76" x2="${(x+cw-8).toFixed(1)}" y2="76" stroke="${stroke}" stroke-width="1.2" opacity="0.45"/>`;
      return body + bevel + crownG;
    }

    // Resolve stroke + crown colour from material (gold→yellow, white gold/steel→dark).
    function strokeColors(type, material) {
      const mat = (material || '').toLowerCase();
      const isTwoTone = /rolesor|two-tone/.test(mat) || (mat.includes('gold') && mat.includes('steel'));
      const isAllGold = /yellow gold|rose gold|everose/.test(mat) && !mat.includes('steel') && !mat.includes('rolesor');
      if (type === 'president' || isAllGold) return { stroke: GOLD, crown: GOLD };
      if (isTwoTone) return { stroke: DARK, crown: GOLD };
      return { stroke: DARK, crown: DARK };
    }

    function renderLinkBars(type, leftCount, rightCount, entry) {
      const t = (type || 'Oyster').toLowerCase();
      const key = (t in LINKW) ? t : 'oyster';
      const { stroke, crown } = strokeColors(key, entry && entry.material);
      const lw = LINKW[key], ew = ENDW[key], cw = CLASPW[key];
      const parts = [];
      let x = 0;
      parts.push(endPiece(x, ew, stroke, false)); x += ew + 1;
      for (let i = 0; i < leftCount; i++) { parts.push(linkShape(key, x, lw, stroke)); x += lw; }
      parts.push(claspShape(x, cw, stroke, crown)); x += cw + 1;
      for (let i = 0; i < rightCount; i++) { parts.push(linkShape(key, x, lw, stroke)); x += lw; }
      parts.push(endPiece(x, ew, stroke, true)); x += ew;
      return `<svg class="rolex-guide__bars-svg" viewBox="-5 -5 ${x + 10} ${BH + 10}" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${esc(type)} bracelet — ${leftCount + rightCount} removable links">${parts.join('')}</svg>`;
    }

    function renderOysterflex() {
      const W = 440;
      let ribs = '';
      for (let i = 46; i < W - 46; i += 15) {
        ribs += `<line x1="${i}" y1="24" x2="${i}" y2="76" stroke="${DARK}" stroke-width="1.4" opacity="0.5"/>`;
      }
      return `<svg class="rolex-guide__bars-svg" viewBox="-5 -5 ${W + 10} ${BH + 10}" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Oysterflex strap — no removable links">
        <rect x="14" y="18" width="${W - 28}" height="64" rx="20" fill="none" stroke="${DARK}" stroke-width="${SW}"/>${ribs}
      </svg>`;
    }

    // -------- Helpers --------
    function braceletTone(entry) {
      const mat = (entry.material || '').toLowerCase();
      const type = entry.bracelet_type || '';
      if (type === 'President') return 'gold';
      if (/^(yellow|rose|everose|white) gold|platinum/.test(mat) && !mat.includes('steel') && !mat.includes('rolesor')) return 'gold';
      if (/rolesor|two-tone/.test(mat) || (mat.includes('gold') && mat.includes('steel'))) return 'twotone';
      return 'steel';
    }

    function braceletShortName(entry) {
      const ref = entry.bracelet_reference;
      const type = entry.bracelet_type;
      if (type === 'Oysterflex') return 'Oysterflex';
      return ref ? `Ref. ${ref}` : type;
    }

    function appendQuery(url, params) {
      const u = new URL(url, location.origin);
      Object.entries(params).forEach(([k, v]) => { if (v != null) u.searchParams.set(k, String(v)); });
      return u.pathname + (u.search || '') + (u.hash || '');
    }

    function esc(s) {
      return String(s ?? '').replace(/[&<>"']/g, c => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
      }[c]));
    }
  }
})();
